import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { PageHeader } from "../components/GradientCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { openUpload, uploadFile, MAX_UPLOAD_MB } from "../lib/uploads";
import { FileText, UserPlus, ExternalLink, Trash2, Check, X, ShieldCheck, Paperclip, Plus, ShoppingCart, Upload } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import QuoteRequestDialog from "../components/QuoteRequestDialog";
import QuoteWorksheetDialog from "../components/QuoteWorksheetDialog";
import { LetterheadPanel } from "./Offboarding";
import CompanyQuoteSettings from "../components/CompanyQuoteSettings";
import { downloadQuoteExport } from "../components/GenerateQuoteDialog";
import { fmtIST } from "../components/QuoteWorksheetDialog";

const TABS = [
  { key: "Mine", label: "My Requests" },
  { key: "Requested", label: "Requested" },
  { key: "Working", label: "Working" },
  { key: "Approval", label: "Approval" },
  { key: "Approved", label: "Approved" },
];

const MINE_FILTERS = [
  { key: "Requested", label: "Requested", stages: ["Requested"], tone: "bg-amber-500" },
  { key: "Working", label: "Working", stages: ["Working", "Disapproved"], tone: "bg-blue-600" },
  { key: "Submitted", label: "In Approval", stages: ["Submitted"], tone: "bg-violet-600" },
  { key: "Approved", label: "Approved", stages: ["Approved"], tone: "bg-emerald-600" },
];

function MineFilterChips({ rows, value, onChange }) {
  const chip = (key, label, count, tone) => (
    <button
      key={key || "all"}
      type="button"
      onClick={() => onChange(key)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${value === key ? `${tone} text-white border-transparent` : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-slate-400"}`}
      data-testid={`quote-mine-chip-${(key || "all").toLowerCase()}`}
    >
      {label} <span className="mono opacity-80">{count}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="quote-mine-chips">
      {chip("", "All", rows.length, "bg-slate-800")}
      {MINE_FILTERS.map((f) => chip(f.key, f.label, rows.filter((r) => f.stages.includes(r.stage)).length, f.tone))}
    </div>
  );
}

function FinalQuoteLink({ r }) {
  if ((r.final_quote_files || []).length > 1 || r.final_quote_files?.[0]?.level) {
    return (
      <span className="inline-flex items-center gap-1">
        {r.final_quote_files.map((f) => (
          <button key={f.level} type="button" onClick={() => openUpload(f.id).catch(() => toast.error("Could not open file"))} className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${f.level === "L1" ? "bg-emerald-100 text-emerald-800" : f.level === "L2" ? "bg-rose-100 text-rose-800" : "bg-teal-100 text-teal-800"}`} title={`${f.company_name} · ${f.code}`} data-testid={`quote-file-${f.level}-${r.id}`}>{f.level}</button>
        ))}
      </span>
    );
  }
  if (r.final_quote_file?.id) {
    return (
      <button type="button" onClick={() => openUpload(r.final_quote_file.id).catch(() => toast.error("Could not open file"))} className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-[11px] font-semibold px-1.5" data-testid={`quote-file-${r.id}`} title={r.final_quote_file.original_filename}>
        <FileText size={11} /> Quote
      </button>
    );
  }
  if (!r.final_quote_url) return null;
  return (
    <a href={r.final_quote_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-[11px] font-semibold px-1.5" data-testid={`quote-url-${r.id}`}>
      <ExternalLink size={11} /> Quote
    </a>
  );
}

function OrderAction({ r, canConvert, onToOrder }) {
  if (r.converted_order_no) {
    return <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]" data-testid={`quote-order-badge-${r.id}`}>Order {r.converted_order_no}</Badge>;
  }
  if (r.stage !== "Approved" || !canConvert) return null;
  return (
    <Button size="sm" className="h-7 px-2 text-[10.5px] bg-sky-600 hover:bg-sky-700 text-white" onClick={() => onToOrder(r)} data-testid={`quote-to-order-${r.id}`}>
      <ShoppingCart size={11} className="mr-1" /> Create Order
    </Button>
  );
}

function InternalRemark({ r, tab }) {
  const txt = r.worksheet?.internal_remark;
  if (!txt || !["Working", "Submitted", "Approved"].includes(tab)) return null;
  return <div className="mt-1 max-w-xs rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10.5px] text-amber-900 whitespace-pre-wrap" data-testid={`quote-internal-remark-${r.id}`}><span className="font-semibold">Internal:</span> {txt}</div>;
}

function ApprovedCodeSearch() {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    e?.preventDefault();
    if (code.length !== 9) { toast.error("Quote codes are 9 letters/digits"); return; }
    setBusy(true);
    try { const { data } = await api.get("/quote-exports/search", { params: { code: code.trim() } }); setRes(data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3 dark:bg-slate-900 dark:border-slate-700" data-testid="quote-code-search">
      <form onSubmit={search} className="flex flex-wrap items-center gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9))} placeholder="Enter 9-character Master Code or Quote Code" className="h-9 max-w-md mono text-[12px]" data-testid="quote-code-search-input" />
        <Button type="submit" size="sm" disabled={busy} className="h-9" data-testid="quote-code-search-btn">{busy ? "Searching…" : "Search"}</Button>
        {res && <Button type="button" size="sm" variant="ghost" className="h-9" onClick={() => { setRes(null); setCode(""); }} data-testid="quote-code-search-clear">Clear</Button>}
      </form>
      {res && (res.exports.length === 0 ? (
        <div className="mt-2 text-[12px] text-slate-500" data-testid="quote-code-search-empty">No quote found for this code.</div>
      ) : (
        <div className="mt-3 overflow-x-auto" data-testid="quote-code-search-results">
          <div className="mb-1.5 text-[11px] text-slate-500">Master Code <span className="mono font-bold text-slate-800">{res.master_code}</span> · {res.exports[0].client_name}</div>
          <table className="w-full min-w-[620px] text-[12px]">
            <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><th className="px-2 py-1.5 text-left">Level</th><th className="px-2 py-1.5 text-left">Code</th><th className="px-2 py-1.5 text-left">Company</th><th className="px-2 py-1.5 text-left">Created By</th><th className="px-2 py-1.5 text-left">Created At</th><th className="px-2 py-1.5 text-right">Grand Total</th><th className="px-2 py-1.5" /></tr></thead>
            <tbody>
              {res.exports.map((x) => (
                <tr key={x.id} className={`border-t border-slate-100 ${x.code === res.matched ? "bg-amber-50" : ""}`} data-testid={`quote-code-row-${x.level}`}>
                  <td className="px-2 py-1.5">{x.is_original ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">L1 · Original</Badge> : <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{x.level}</Badge>}</td>
                  <td className="px-2 py-1.5 mono font-semibold">{x.code}</td>
                  <td className="px-2 py-1.5">{x.company_name}</td>
                  <td className="px-2 py-1.5">{x.created_by_name}</td>
                  <td className="px-2 py-1.5 mono">{fmtIST(x.created_at)}</td>
                  <td className="px-2 py-1.5 text-right mono">₹ {Number(x.grand_total || 0).toLocaleString("en-IN")}</td>
                  <td className="px-2 py-1.5 text-right"><Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => downloadQuoteExport(x).catch(() => toast.error("You don't have access to download this PDF"))} data-testid={`quote-code-download-${x.level}`}>PDF</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const STAGE_TONE = {
  Requested: "bg-amber-100 text-amber-800",
  Working: "bg-blue-100 text-blue-800",
  Submitted: "bg-violet-100 text-violet-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Disapproved: "bg-rose-100 text-rose-800",
};

export default function Quotation() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [tab, setTab] = useState("Mine");
  const [rows, setRows] = useState([]);
  const [access, setAccess] = useState({ requested: false, working: false, approval: false, approved: false });
  const [employees, setEmployees] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [submitTarget, setSubmitTarget] = useState(null);
  const [decideTarget, setDecideTarget] = useState(null);
  const [logsTarget, setLogsTarget] = useState(null);
  const [tabAccessOpen, setTabAccessOpen] = useState(false);
  const [tabAccessUsers, setTabAccessUsers] = useState([]);
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [adminTab, setAdminTab] = useState("Requested");
  const [wsTarget, setWsTarget] = useState(null);
  const [mineFilter, setMineFilter] = useState("");
  const navigate = useNavigate();
  const toOrder = (r) => navigate("/m/sales_orders", { state: { fromQuote: r } });
  const [workingAssignee, setWorkingAssignee] = useState("");
  const assigneeOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.assigned_to_id) m.set(r.assigned_to_id, r.assigned_to_name || "—"); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const shownRows = useMemo(() => {
    if (tab === "Working") return workingAssignee ? rows.filter((r) => r.assigned_to_id === workingAssignee) : rows;
    if (tab !== "Mine" || !mineFilter) return rows;
    return rows.filter((r) => (MINE_FILTERS.find((f) => f.key === mineFilter)?.stages || []).includes(r.stage));
  }, [rows, tab, mineFilter, workingAssignee]);

  const [myTabs, setMyTabs] = useState([]);
  const probeAccess = async () => {
    try {
      const { data } = await api.get("/quote-work/my-tab-access");
      const t = data?.tabs || [];
      setMyTabs(t);
      setAccess({ requested: t.includes("requested"), working: t.includes("working"), approval: t.includes("approval"), approved: t.includes("approved") });
      if (t.length) setAdminTab((cur) => (t.includes(cur.toLowerCase()) ? cur : TABS.find((x) => x.key.toLowerCase() === t[0]).key));
    } catch { setMyTabs([]); }
  };

  const claimQuote = async (r) => {
    try {
      await api.post(`/quote-requests/${r.id}/claim`);
      toast.success("Assigned to you — moved to Working");
      setAdminTab("Working"); setTab("Working");
      probeAccess();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); load(); }
  };

  const load = async () => {
    if (tab === "Company") return;
    try {
      const { data } = await api.get(`/quote-requests?stage=${tab}`);
      setRows(data || []);
    } catch (e) {
      if (e.response?.status === 403) setRows([]);
      else toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => { probeAccess(); }, []);
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const adminTabs = [
    ...TABS.filter((t) => t.key !== "Mine" && myTabs.includes(t.key.toLowerCase())),
    ...(isMaster ? [{ key: "Company", label: "Company Setup" }] : []),
  ];
  const renderTable = (key) => (
    <QuoteTable
      rows={shownRows}
      onToOrder={toOrder}
      onClaim={claimQuote}
      onWorksheet={setWsTarget}
      canClaim={access.requested}
      tab={key}
      isMaster={isMaster}
      currentUserId={user?.id}
      onAssign={(r) => { loadEmployees(); setAssignTarget(r); }}
      onSubmit={(r) => setSubmitTarget(r)}
      onDecide={(r) => setDecideTarget(r)}
      onLogs={(r) => setLogsTarget(r)}
      onDelete={(r) => setPendingDeleteQuote(r)}
      hasApprovalAccess={isMaster || access.approval}
    />
  );

  const loadEmployees = async () => {
    if (employees.length) return;
    try {
      const { data } = await api.get("/employees-lite");
      setEmployees(data || []);
      setTabAccessUsers(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const [pendingDeleteQuote, setPendingDeleteQuote] = useState(null);
  const confirmDeleteQuote = async (reason) => {
    try {
      await api.delete(`/quote-requests/${pendingDeleteQuote.id}`, { data: { reason } });
      toast.success("Quote request moved to Recycle Bin");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); throw e; }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        moduleKey="quotation"
        title="Quotation"
        eyebrow="Sales workflow"
        subtitle="Track every quote from CRM request to approval and delivery."
        icon={FileText}
        actions={
          <Button onClick={() => setNewQuoteOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quote-new-request">
            <Plus size={14} className="mr-1" /> New Quote Request
          </Button>
        }
      />
      <QuoteWorksheetDialog target={wsTarget} onOpenChange={(o) => { if (!o) setWsTarget(null); }} onSaved={(d) => { setWsTarget(d); load(); }} />
      <QuoteRequestDialog open={newQuoteOpen} onOpenChange={setNewQuoteOpen} onCreated={() => { if (tab === "Mine") load(); else setTab("Mine"); }} />

      <Tabs value={tab === "Mine" ? "Mine" : "Admin"} onValueChange={(v) => setTab(v === "Mine" ? "Mine" : adminTab)}>
        <TabsList>
          <TabsTrigger value="Mine" data-testid="quote-tab-mine">My Requests</TabsTrigger>
          {adminTabs.length > 0 && <TabsTrigger value="Admin" data-testid="quote-tab-admin"><ShieldCheck size={12} className="mr-1" /> Admin</TabsTrigger>}
        </TabsList>
        <TabsContent value="Mine" className="mt-3">
          <MineFilterChips rows={rows} value={mineFilter} onChange={setMineFilter} />
          {renderTable("Mine")}
        </TabsContent>
        {adminTabs.length > 0 && (
          <TabsContent value="Admin" className="mt-3">
            <Tabs value={tab === "Mine" ? adminTab : tab} onValueChange={(v) => { setAdminTab(v); setTab(v); }}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <TabsList className="bg-slate-100/70" data-testid="quote-admin-subtabs">
                  {adminTabs.map((t) => (
                    <TabsTrigger key={t.key} value={t.key} data-testid={`quote-tab-${t.key.toLowerCase()}`}>{t.label}</TabsTrigger>
                  ))}
                </TabsList>
                {isMaster && (
                  <Button size="sm" variant="outline" onClick={() => { loadEmployees(); setTabAccessOpen(true); }} className="h-8 text-xs" data-testid="quote-manage-access">
                    <ShieldCheck size={12} className="mr-1" /> Manage Access
                  </Button>
                )}
              </div>
              {adminTabs.map((t) => (
                <TabsContent key={t.key} value={t.key} className="mt-3">
                  {t.key === "Working" && isMaster && (
                    <div className="flex items-center gap-2 mb-3" data-testid="quote-working-filter">
                      <span className="text-[10px] uppercase font-bold text-slate-500">Assigned To</span>
                      <select value={workingAssignee} onChange={(e) => setWorkingAssignee(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px] dark:bg-slate-900 dark:border-slate-700" data-testid="quote-working-assignee-select">
                        <option value="">All users ({rows.length})</option>
                        {assigneeOptions.map(([id, name]) => (
                          <option key={id} value={id}>{name} ({rows.filter((r) => r.assigned_to_id === id).length})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {t.key === "Working" && !isMaster && <div className="text-[11px] text-slate-500 mb-2">Showing quote requests assigned to you.</div>}
                  {t.key === "Approved" && <ApprovedCodeSearch />}
                  {t.key === "Company" ? <><LetterheadPanel isMaster={isMaster} /><CompanyQuoteSettings /></> : renderTable(t.key)}
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>
        )}
      </Tabs>

      <DeleteWithReasonDialog
        open={!!pendingDeleteQuote}
        onClose={() => setPendingDeleteQuote(null)}
        title={pendingDeleteQuote ? `Delete quote request for ${pendingDeleteQuote.client_name}?` : "Delete quote?"}
        description="It will move to Admin Hub → Recycle Bin. Master Admin can restore it later."
        onConfirm={confirmDeleteQuote}
        testid="quote-delete"
      />

      <QuoteAssignDialog target={assignTarget} employees={employees} onOpenChange={(o) => { if (!o) setAssignTarget(null); }} onSaved={() => { setAssignTarget(null); load(); }} />
      <QuoteSubmitDialog target={submitTarget} onOpenChange={(o) => { if (!o) setSubmitTarget(null); }} onSaved={() => { setSubmitTarget(null); load(); }} />
      <QuoteDecideDialog target={decideTarget} onOpenChange={(o) => { if (!o) setDecideTarget(null); }} onSaved={() => { setDecideTarget(null); load(); }} />
      <QuoteLogsDialog target={logsTarget} onOpenChange={(o) => { if (!o) setLogsTarget(null); }} onUpdated={(d) => { setLogsTarget(d); load(); }} />
      <QuoteTabAccessDialog open={tabAccessOpen} onOpenChange={setTabAccessOpen} users={tabAccessUsers} />
    </div>
  );
}

function QuoteTable({ rows, tab, isMaster, currentUserId, onAssign, onSubmit, onDecide, onLogs, onDelete, hasApprovalAccess, onToOrder, onClaim, canClaim, onWorksheet }) {
  const [viewMode] = useGlobalViewMode();
  if (viewMode === "card") {
    if (rows.length === 0) return <div className="pk-card p-12 text-center text-slate-400 text-sm italic">No quotes in this tab.</div>;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="quote-cards">
        {rows.map((r) => {
          const isAssignee = r.assigned_to_id === currentUserId;
          return (
            <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`quote-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900 truncate">{r.client_name}</div>
                  <div className="text-[10px] text-slate-500 mono">{r.u_dise || "—"} · {r.client_type || "—"}</div>
                  <InternalRemark r={r} tab={tab} />
                </div>
                <Badge className={(STAGE_TONE[r.stage] || "bg-slate-100 text-slate-700") + " text-[10px] shrink-0 hover:opacity-90"}>{r.stage}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-3">
                <div><div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Sales Assoc</div><div className="text-slate-800">{r.sales_associate_name || "—"}</div></div>
                <div><div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Requester</div><div className="text-slate-800">{r.requester_name || "—"}</div></div>
                <div><div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Order Value</div><div className="mono text-slate-800">{r.order_value || "—"}</div></div>
                <div><div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Assigned</div><div className="text-slate-800">{r.assigned_to_name || <span className="text-slate-400 italic">—</span>}</div></div>
                <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Deadline</div><div className="mono text-[11px] text-slate-600">{r.deadline ? new Date(r.deadline).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div></div>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
                {!r.is_limited && <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onLogs(r)} data-testid={`quote-logs-${r.id}`}>Logs</Button>}
                <FinalQuoteLink r={r} />
                <OrderAction r={r} canConvert={isMaster || r.requester_id === currentUserId} onToOrder={onToOrder} />
                {tab === "Requested" && canClaim && (
                  <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onClaim(r)} data-testid={`quote-claim-${r.id}`}>Assign Me</Button>
                )}
                {tab === "Requested" && isMaster && (
                  <Button size="sm" className="h-7 px-2 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onAssign(r)} data-testid={`quote-assign-${r.id}`}>
                    <UserPlus size={11} className="mr-1" /> Assign
                  </Button>
                )}
                {tab === "Working" && isMaster && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => onAssign(r)} data-testid={`quote-reassign-${r.id}`}><UserPlus size={11} className="mr-1" /> Reassign</Button>
                )}
                {tab === "Working" && (isMaster || isAssignee) && (r.stage === "Working" || r.stage === "Disapproved") && (
                  <Button size="sm" className="h-7 px-2 text-[10.5px] bg-slate-900 hover:bg-slate-800 text-white" onClick={() => onWorksheet(r)} data-testid={`quote-worksheet-${r.id}`}>{r.worksheet?.items?.length ? "Edit Worksheet" : "Start Working"}</Button>
                )}
                {tab === "Working" && (isMaster || isAssignee) && (r.stage === "Working" || r.stage === "Disapproved") && (
                  <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onSubmit(r)} data-testid={`quote-submit-btn-${r.id}`}>Submit</Button>
                )}
                {tab === "Approval" && hasApprovalAccess && r.stage === "Submitted" && (
                  <>
                    <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onDecide({ ...r, _decision: "approve" })} data-testid={`quote-approve-${r.id}`}>
                      <Check size={11} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onDecide({ ...r, _decision: "disapprove" })} data-testid={`quote-disapprove-${r.id}`}>
                      <X size={11} className="mr-1" /> Disapprove
                    </Button>
                  </>
                )}
                {isMaster && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(r)} data-testid={`quote-delete-${r.id}`}>
                    <Trash2 size={11} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr>
            <th className="px-4 py-3 text-left pk-table-header">Client</th>
            <th className="px-4 py-3 text-left pk-table-header">Sales Associate</th>
            <th className="px-4 py-3 text-left pk-table-header">Requester</th>
            <th className="px-4 py-3 text-left pk-table-header">Order Value</th>
            <th className="px-4 py-3 text-left pk-table-header">Deadline</th>
            <th className="px-4 py-3 text-left pk-table-header">Stage</th>
            <th className="px-4 py-3 text-left pk-table-header">Assigned To</th>
            <th className="px-4 py-3 text-right pk-table-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (<tr><td colSpan={8} className="text-center py-10 text-slate-400 text-sm italic">{tab === "Mine" ? "You haven't raised any quote requests yet." : "No quotes in this tab."}</td></tr>)}
          {rows.map((r) => {
            const isAssignee = r.assigned_to_id === currentUserId;
            const isRequester = r.requester_id === currentUserId;
            return (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`quote-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{r.client_name}</div>
                  <div className="text-[10px] text-slate-500 mono">{r.u_dise || "—"} · {r.client_type || "—"}</div>
                  <InternalRemark r={r} tab={tab} />
                </td>
                <td className="px-4 py-3 text-[12px]">{r.sales_associate_name || "—"}</td>
                <td className="px-4 py-3 text-[12px]">{r.requester_name || "—"}</td>
                <td className="px-4 py-3 text-[12px] mono">{r.order_value || "—"}</td>
                <td className="px-4 py-3 text-[11px] mono text-slate-600">{r.deadline ? new Date(r.deadline).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                <td className="px-4 py-3">
                  <Badge className={(STAGE_TONE[r.stage] || "bg-slate-100 text-slate-700") + " text-[10px] hover:opacity-90"}>{r.stage}</Badge>
                </td>
                <td className="px-4 py-3 text-[12px]">{r.assigned_to_name || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {!r.is_limited && <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onLogs(r)} data-testid={`quote-logs-${r.id}`}>Logs</Button>}
                    <FinalQuoteLink r={r} />
                    <OrderAction r={r} canConvert={isMaster || isRequester} onToOrder={onToOrder} />
                    {tab === "Requested" && canClaim && (
                      <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onClaim(r)} data-testid={`quote-claim-${r.id}`}>Assign Me</Button>
                    )}
                    {tab === "Requested" && isMaster && (
                      <Button size="sm" className="h-7 px-2 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onAssign(r)} data-testid={`quote-assign-${r.id}`}>
                        <UserPlus size={11} className="mr-1" /> Assign
                      </Button>
                    )}
                    {tab === "Working" && isMaster && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => onAssign(r)} data-testid={`quote-reassign-${r.id}`}><UserPlus size={11} className="mr-1" /> Reassign</Button>
                    )}
                    {tab === "Working" && (isMaster || isAssignee) && (r.stage === "Working" || r.stage === "Disapproved") && (
                      <Button size="sm" className="h-7 px-2 text-[10.5px] bg-slate-900 hover:bg-slate-800 text-white" onClick={() => onWorksheet(r)} data-testid={`quote-worksheet-${r.id}`}>{r.worksheet?.items?.length ? "Edit Worksheet" : "Start Working"}</Button>
                    )}
                    {tab === "Working" && (isMaster || isAssignee) && (r.stage === "Working" || r.stage === "Disapproved") && (
                      <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onSubmit(r)} data-testid={`quote-submit-btn-${r.id}`}>
                        Submit
                      </Button>
                    )}
                    {tab === "Approval" && hasApprovalAccess && r.stage === "Submitted" && (
                      <>
                        <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onDecide({ ...r, _decision: "approve" })} data-testid={`quote-approve-${r.id}`}>
                          <Check size={11} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onDecide({ ...r, _decision: "disapprove" })} data-testid={`quote-disapprove-${r.id}`}>
                          <X size={11} className="mr-1" /> Disapprove
                        </Button>
                      </>
                    )}
                    {isMaster && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(r)} data-testid={`quote-delete-${r.id}`}>
                        <Trash2 size={11} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function QuoteAssignDialog({ target, employees, onOpenChange, onSaved }) {
  const open = !!target;
  const [uid, setUid] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setUid(""); }, [open]);
  if (!open) return null;
  const submit = async () => {
    if (!uid) { toast.error("Pick a user"); return; }
    setBusy(true);
    try { await api.patch(`/quote-requests/${target.id}/assign`, { assigned_to_id: uid }); toast.success(target.assigned_to_id ? "Reassigned" : "Assigned"); onSaved?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quote-assign-dialog">
        <DialogHeader>
          <DialogTitle className="heading">{target.assigned_to_id ? "Reassign" : "Assign"} Quote — {target.client_name}</DialogTitle>
          {target.assigned_to_id && <div className="text-[11.5px] text-slate-500 mt-1">Currently assigned to <strong>{target.assigned_to_name}</strong></div>}
        </DialogHeader>
        <div className="space-y-2 mt-2 max-h-72 overflow-y-auto">
          {(employees || []).filter((u) => u.id !== target.assigned_to_id).map((u) => (
            <label key={u.id} className={`flex items-center gap-2 px-2 py-1.5 rounded border ${uid === u.id ? "border-indigo-400 bg-indigo-50" : "border-slate-200"}`}>
              <input type="radio" checked={uid === u.id} onChange={() => setUid(u.id)} className="accent-indigo-600" data-testid={`quote-assign-user-${u.id}`} />
              <div className="flex-1 min-w-0"><div className="text-[13px] font-semibold">{u.name}</div><div className="text-[10px] text-slate-500">{u.email}</div></div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quote-assign-submit">{busy ? "…" : target.assigned_to_id ? "Reassign" : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LEVELS = [["L1", "L1 Quote (original)", true], ["L2", "L2 Quote", false], ["L3", "L3 Quote", false]];

function QuoteSubmitDialog({ target, onOpenChange, onSaved }) {
  const open = !!target;
  const [slots, setSlots] = useState({});
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSlots({}); setRemark(""); } }, [open, target?.id]);
  if (!open) return null;
  const setSlot = (lv, patch) => setSlots((p) => ({ ...p, [lv]: { ...(p[lv] || {}), ...patch } }));
  const submit = async () => {
    for (const [lv, , req] of LEVELS) {
      const sl = slots[lv] || {};
      if (req && !sl.file) { toast.error(`Upload the ${lv} quote PDF`); return; }
      if (sl.file && !/^[A-Z0-9]{9}$/.test(sl.code || "")) { toast.error(`${lv}: enter the 9-character quote code printed on the PDF`); return; }
      if (!sl.file && sl.code) { toast.error(`${lv}: upload the PDF for code ${sl.code}`); return; }
    }
    if (!remark.trim()) { toast.error("Add a submission remark"); return; }
    setBusy(true);
    try {
      const final_quotes = [];
      for (const [lv] of LEVELS) {
        const sl = slots[lv];
        if (!sl?.file) continue;
        final_quotes.push({ level: lv, code: sl.code, file: await uploadFile(sl.file, { purpose: "quote_final", ref_id: target.id }) });
      }
      await api.patch(`/quote-requests/${target.id}/submit`, { final_quotes, remark: remark.trim() });
      toast.success("Submitted for approval");
      onSaved?.();
    } catch (e) { toast.error(e.response ? formatError(e.response?.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] [&>*]:min-w-0" data-testid="quote-submit-dialog">
        <DialogHeader>
          <DialogTitle className="heading pr-6">Submit Quote — {target.client_name}</DialogTitle>
          {target.approval_remark && target.stage === "Disapproved" && (
            <div className="text-[11.5px] bg-rose-50 border border-rose-200 text-rose-900 rounded-md px-3 py-2 mt-2"><strong>Previous Disapproval:</strong> {target.approval_remark}</div>
          )}
        </DialogHeader>
        <div className="space-y-2.5 mt-1">
          {LEVELS.map(([lv, label, req]) => {
            const sl = slots[lv] || {};
            return (
              <div key={lv} className="rounded-md border border-slate-200 p-2.5" data-testid={`quote-submit-slot-${lv}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}{req ? " *" : " (optional)"}</span>
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50/50 px-2 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-50" data-testid={`quote-submit-upload-${lv}`}>
                    <Upload size={11} /> {sl.file ? "Replace PDF" : "Upload PDF"}
                    <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`quote-submit-input-${lv}`}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (!f) return;
                        if (!/\.pdf$/i.test(f.name)) { toast.error("Only PDF files are allowed"); return; }
                        if (f.size > MAX_UPLOAD_MB * 1024 * 1024) { toast.error(`File exceeds ${MAX_UPLOAD_MB} MB`); return; }
                        setSlot(lv, { file: f });
                      }} />
                  </label>
                </div>
                {sl.file && <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-slate-600"><Paperclip size={11} className="shrink-0 text-slate-400" /><span className="truncate">{sl.file.name}</span></div>}
                <Input value={sl.code || ""} onChange={(e) => setSlot(lv, { code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9) })} placeholder="Quote code (9 characters, printed on the PDF)" className="mt-1.5 h-8 mono text-[12px]" data-testid={`quote-submit-code-${lv}`} />
              </div>
            );
          })}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Submission Remark *</Label>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} className="mt-1.5 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700" data-testid="quote-submit-remark" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quote-submit-confirm">{busy ? "Uploading…" : "Submit for Approval"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDecideDialog({ target, onOpenChange, onSaved }) {
  const open = !!target;
  const decision = target?._decision || "approve";
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setRemark(""); }, [open]);
  if (!open) return null;
  const submit = async () => {
    if (decision === "disapprove" && !remark.trim()) { toast.error("Add a disapproval remark"); return; }
    setBusy(true);
    try { await api.patch(`/quote-requests/${target.id}/decide`, { decision, remark: remark.trim() }); toast.success(decision === "approve" ? "Approved" : "Sent back to assignee"); onSaved?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quote-decide-dialog">
        <DialogHeader><DialogTitle className="heading">{decision === "approve" ? "Approve" : "Disapprove"} Quote — {target.client_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          {target.final_quote_url && (
            <a href={target.final_quote_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline text-[12px] inline-flex items-center gap-1"><ExternalLink size={11} /> Open submitted quote</a>
          )}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Remark {decision === "disapprove" ? "*" : "(optional)"}</Label>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} className="mt-1.5 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700" data-testid="quote-decide-remark" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className={(decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700") + " text-white"} data-testid="quote-decide-confirm">{busy ? "…" : (decision === "approve" ? "Approve" : "Disapprove")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteLogsDialog({ target, onOpenChange, onUpdated }) {
  const open = !!target;
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setDraft(""); }, [open]);
  if (!open) return null;
  const logs = (target.logs || []).slice().reverse();
  const post = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/quote-requests/${target.id}/remark`, { remark: draft.trim() }); toast.success("Remark added"); setDraft(""); onUpdated?.(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quote-logs-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Quote Logs — {target.client_name}</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">Stage: <strong>{target.stage}</strong></div>
        </DialogHeader>
        <div className="rounded-md border border-slate-200 bg-slate-50/40 px-3 py-2 text-[11.5px] space-y-1 mt-2">
          <div><span className="font-semibold">Sales Associate:</span> {target.sales_associate_name || "—"}</div>
          <div><span className="font-semibold">Requester:</span> {target.requester_name}</div>
          <div><span className="font-semibold">Order Value:</span> {target.order_value || "—"}</div>
          <div><span className="font-semibold">Commission:</span> {target.commission || "—"}</div>
          {target.po_url && (<div><span className="font-semibold">PO:</span> <a href={target.po_url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">{target.po_url}</a></div>)}
          {(target.attachments || []).length > 0 && (
            <div data-testid="quote-attachments">
              <span className="font-semibold">Files:</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {target.attachments.map((a) => (
                  <button key={a.id} type="button" onClick={() => openUpload(a.id).catch(() => toast.error("Could not open file"))} className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-white px-2 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-50" data-testid={`quote-attachment-${a.id}`}>
                    <Paperclip size={11} /> {a.original_filename}
                  </button>
                ))}
              </div>
            </div>
          )}
          {target.items_remark && (<div className="whitespace-pre-wrap"><span className="font-semibold">Items / Remark:</span> {target.items_remark}</div>)}
          {target.important_remark && (<div className="whitespace-pre-wrap text-rose-800"><span className="font-semibold">Important:</span> {target.important_remark}</div>)}
        </div>
        <div className="space-y-2 mt-3 max-h-[40vh] overflow-y-auto pr-1">
          {logs.map((l, i) => (
            <div key={i} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-[12px]">
              <div className="flex items-center justify-between"><div className="font-semibold text-slate-800">{l.by_name || "—"}</div><Badge variant="outline" className="text-[10px]">{l.action}</Badge></div>
              <div className="text-[10.5px] text-slate-500 mono">{l.at ? new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
              {l.remark && <div className="text-[12.5px] text-slate-700 mt-1 whitespace-pre-wrap">{l.remark}</div>}
            </div>
          ))}
        </div>
        <div className="border-t pt-3 mt-3 space-y-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Add a remark…" className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700" data-testid="quote-log-input" />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={post} disabled={busy || !draft.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quote-log-submit">{busy ? "…" : "Post"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuoteTabAccessDialog({ open, onOpenChange, users }) {
  const [grants, setGrants] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/quote-work/tab-access");
        const map = {};
        (data || []).forEach((g) => {
          const tabs = g.tabs || [];
          map[g.user_id] = { requested: tabs.includes("requested"), working: tabs.includes("working"), approval: tabs.includes("approval"), approved: tabs.includes("approved") };
        });
        setGrants(map);
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    })();
  }, [open]);
  const toggle = (uid, k) => setGrants((g) => ({ ...g, [uid]: { ...(g[uid] || {}), [k]: !((g[uid] || {})[k]) } }));
  const save = async (uid) => {
    setBusy(true);
    try {
      const cur = grants[uid] || {};
      const tabs = ["requested", "working", "approval", "approved"].filter((k) => cur[k]);
      await api.post("/quote-work/tab-access", { user_id: uid, tabs });
      toast.success("Saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="quote-tab-access-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Quotation · Tab Access</DialogTitle>
          <div className="text-[11.5px] text-slate-500 mt-1">Give a user any tab here and they will see the Admin tab. Users with Requested access see basic details and can click "Assign Me". Everyone always sees their own requests under My Requests.</div>
        </DialogHeader>
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50/60 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-2 py-2 text-center">Requested</th>
              <th className="px-2 py-2 text-center">Working</th>
              <th className="px-2 py-2 text-center">Approval</th>
              <th className="px-2 py-2 text-center">Approved</th>
              <th className="px-2 py-2 text-right">—</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u) => { const g = grants[u.id] || {}; return (
              <tr key={u.id} className="border-t" data-testid={`quote-tab-access-row-${u.id}`}>
                <td className="px-3 py-2"><div className="font-semibold text-slate-800">{u.name}</div><div className="text-[10px] text-slate-500">{u.email}</div></td>
                <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.requested} onChange={() => toggle(u.id, "requested")} data-testid={`quote-tab-access-requested-${u.id}`} /></td>
                <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.working} onChange={() => toggle(u.id, "working")} data-testid={`quote-tab-access-working-${u.id}`} /></td>
                <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.approval} onChange={() => toggle(u.id, "approval")} data-testid={`quote-tab-access-approval-${u.id}`} /></td>
                <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.approved} onChange={() => toggle(u.id, "approved")} data-testid={`quote-tab-access-approved-${u.id}`} /></td>
                <td className="px-2 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-[10.5px]" disabled={busy} onClick={() => save(u.id)} data-testid={`quote-tab-access-save-${u.id}`}>Save</Button></td>
              </tr>
            ); })}
          </tbody>
        </table>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
